import { getDtVite } from '@/lib/runtime-env'

const LOCAL_BACKOFFICE_URL = 'http://localhost:3010'

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1'
}

function isLoopbackUrl(url: string): boolean {
  try {
    return isLoopbackHostname(new URL(url).hostname)
  } catch {
    return false
  }
}

/**
 * Deployed app hosts are `dt.<env>.fairgen.ai`. The backoffice on that
 * environment is `dt-backoffice.<env>.fairgen.ai`.
 */
export function backofficeOriginFromMainAppPage(pageOrigin: string): string | null {
  try {
    const url = new URL(pageOrigin)
    if (isLoopbackHostname(url.hostname)) {
      return null
    }
    if (!url.hostname.startsWith('dt.') || !url.hostname.includes('fairgen.ai')) {
      return null
    }
    url.hostname = url.hostname.replace(/^dt\./, 'dt-backoffice.')
    url.port = ''
    return url.origin
  } catch {
    return null
  }
}

/**
 * Resolve the backoffice base URL for admin redirects.
 * Explicit non-loopback config wins; on deployed hosts derive from the page
 * origin so a dev image baked with localhost:3010 still redirects correctly.
 */
export function resolveBackofficeUrl(input?: {
  pageOrigin?: string
  configuredUrl?: string
}): string {
  const configured =
    (input?.configuredUrl ?? getDtVite('DT_VITE_BACKOFFICE_URL') ?? '')
      .trim()
      .replace(/\/$/, '')
  if (configured && !isLoopbackUrl(configured)) {
    return configured
  }
  const pageOrigin =
    input?.pageOrigin ??
    (typeof window !== 'undefined' ? window.location.origin : '')
  const derived = pageOrigin ? backofficeOriginFromMainAppPage(pageOrigin) : null
  if (derived) {
    return derived
  }
  if (configured) {
    return configured
  }
  return LOCAL_BACKOFFICE_URL
}
